/**
 * GeoJSON export — a pure, dependency-free serializer mirroring kml.ts. The same
 * PointSet the renderer uses in; a valid GeoJSON FeatureCollection string out.
 * Coordinates are [lng, lat] per the GeoJSON spec (RFC 7946).
 *
 * Distinct from the render-time `toGeoJSON` in @/lib/map/pointset, which carries
 * internal `_id`/`_confidence` fields for the map layers — this one emits clean,
 * user-facing properties for export to GIS tools.
 */

import type { Point, PointSet } from "@/lib/map/pointset";

export interface GeoJsonOptions {
  /** Column whose value becomes each feature's `name` property. */
  titleColumn?: string;
  /** Property columns to include on each feature (defaults to every column). */
  fields?: string[];
}

function feature(point: Point, titleColumn: string | undefined, fields: string[]) {
  const properties: Record<string, unknown> = {};
  const title = titleColumn ? point.props[titleColumn] : undefined;
  if (title) properties.name = title;
  for (const f of fields) properties[f] = point.props[f] ?? null;
  return {
    type: "Feature" as const,
    geometry: { type: "Point" as const, coordinates: [point.lng, point.lat] },
    properties,
  };
}

/** Serialize a PointSet to a pretty-printed GeoJSON FeatureCollection string. */
export function toGeoJson(pointSet: PointSet, opts: GeoJsonOptions = {}): string {
  const fields = opts.fields ?? pointSet.columns.map((c) => c.name);
  return JSON.stringify(
    {
      type: "FeatureCollection",
      features: pointSet.points.map((p) => feature(p, opts.titleColumn, fields)),
    },
    null,
    2,
  );
}
